import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { uniqBy } from 'lodash';
import { useToggle } from 'usehooks-ts';

import * as routes from 'router/routes';
import { MENU_ITEM_KEYS, MENU_ITEMS_DATA } from 'constants/menuItems';
import { DIALOG_VARIANT } from 'constants/dialog';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { useCurrentUserInfo } from 'hooks/useCurrentUserInfo';
import { useMatchPathParams } from 'hooks/useMatchPathParams';
import { useDialog } from 'hooks/useDialog';
import { useGetOrganisationByIdQuery } from 'store/entities/organisations/api';
import { logout } from 'store/entities/users/slice';
import { dialogVariantSelector } from 'store/entities/ui/slice';

const SUPER_ADMIN_PAGES = [
  routes.organisationsList,
  routes.manageUsers,
  routes.activityLog,
  routes.organisationOverview,
];
const ACCOUNT_ITEM_KEYS = [MENU_ITEM_KEYS.ACCOUNT_SETTINGS, MENU_ITEM_KEYS.LOGOUT];

const ORGANISATION_SCOPED_ITEM_KEYS = [
  MENU_ITEM_KEYS.PERSONAL_PROJECTS,
  MENU_ITEM_KEYS.ORGANISATION_PROJECTS,
  MENU_ITEM_KEYS.DASHBOARD_PORTFOLIO,
  MENU_ITEM_KEYS.MANAGE_USERS,
  MENU_ITEM_KEYS.ADD_PROJECT,
  MENU_ITEM_KEYS.ACTION_TRACKER,
  MENU_ITEM_KEYS.PROJECT_JOURNALS,
  MENU_ITEM_KEYS.PERSONAL_JOURNALS,
  MENU_ITEM_KEYS.SECURITY_SETTINGS,
  MENU_ITEM_KEYS.ACTIVITY_LOG,
];

const getSuperAdminItemKeys = (isLgUp) => [
  MENU_ITEM_KEYS.ADD_USERS,
  MENU_ITEM_KEYS.MANAGE_USERS,
  ...(isLgUp ? [MENU_ITEM_KEYS.ACTIVITY_LOG, MENU_ITEM_KEYS.ORGANISATION_OVERVIEW] : []),
];

const getOrganisationAdminItemKeys = (
  isLgUp,
  isSmUp,
  personalProjectsCount,
  hasProjectRelations,
  hasPersonalJournals,
) => [
  ...(personalProjectsCount ? [MENU_ITEM_KEYS.PERSONAL_PROJECTS] : []),
  MENU_ITEM_KEYS.ORGANISATION_PROJECTS,
  ...(isLgUp ? [MENU_ITEM_KEYS.DASHBOARD_PORTFOLIO] : []),
  MENU_ITEM_KEYS.ADD_USERS,
  MENU_ITEM_KEYS.MANAGE_USERS,
  ...(isSmUp ? [MENU_ITEM_KEYS.ADD_PROJECT] : []),
  ...(isLgUp && hasProjectRelations ? [MENU_ITEM_KEYS.ACTION_TRACKER, MENU_ITEM_KEYS.PROJECT_JOURNALS] : []),
  ...(isLgUp && hasPersonalJournals ? [MENU_ITEM_KEYS.PERSONAL_JOURNALS] : []),
  MENU_ITEM_KEYS.SECURITY_SETTINGS,
  ...(isLgUp ? [MENU_ITEM_KEYS.ACTIVITY_LOG] : []),
];

const getUserItemKeys = (isLgUp, isDashboardPortfolioEnabled, hasProjectRelations, hasPersonalJournals) => {
  if (!isLgUp) {
    return [];
  }

  const otherItemKeys = [
    ...(isDashboardPortfolioEnabled ? [MENU_ITEM_KEYS.DASHBOARD_PORTFOLIO] : []),
    ...(hasProjectRelations ? [MENU_ITEM_KEYS.ACTION_TRACKER, MENU_ITEM_KEYS.PROJECT_JOURNALS] : []),
    ...(hasPersonalJournals ? [MENU_ITEM_KEYS.PERSONAL_JOURNALS] : []),
  ];

  return otherItemKeys.length ? [MENU_ITEM_KEYS.PERSONAL_PROJECTS, ...otherItemKeys] : [];
};

export const useMenuItems = () => {
  const [isMobileMenuOpen, toggleIsMobileMenuOpen, setIsMobileMenuOpen] = useToggle();

  const dispatch = useDispatch();
  const { openDialog, closeDialog } = useDialog();
  const dialogVariant = useSelector(dialogVariantSelector);

  const { pathname } = useLocation();
  const { organisationId } = useMatchPathParams();
  const { push } = useHistory();

  const { isSuperAdminPermissions, isAdminPermissions, isUserPermissions } = useCurrentUserInfo(organisationId);
  const {
    data: {
      id: selectedOrganisationId,
      personalProjectsCount,
      isDashboardPortfolioEnabled,
      hasProjectRelations,
      hasPersonalJournals,
    } = {},
  } = useGetOrganisationByIdQuery({ organisationId }, { skip: !organisationId });

  const { isLgUp, isSmUp } = useBreakpointFlags();

  const superAdminItemKeys = getSuperAdminItemKeys(isLgUp);

  const organisationItemKeys = getOrganisationAdminItemKeys(
    isLgUp,
    isSmUp,
    personalProjectsCount,
    hasProjectRelations,
    hasPersonalJournals,
  );

  const isSelectedOrganisation =
    !!organisationId && !!selectedOrganisationId && organisationId === selectedOrganisationId;

  const isAddUserDialogOpen = dialogVariant === DIALOG_VARIANT.ADD_USER;

  const activeItemKey = isAddUserDialogOpen
    ? MENU_ITEM_KEYS.ADD_USERS
    : Object.values(MENU_ITEMS_DATA).find(({ link }) => pathname.includes(link))?.itemKey;

  const onItemClick = (itemKey) => {
    const itemLink = MENU_ITEMS_DATA[itemKey].link;
    const link =
      isSelectedOrganisation && ORGANISATION_SCOPED_ITEM_KEYS.includes(itemKey)
        ? `${itemLink}/${organisationId}`
        : itemLink;

    switch (itemKey) {
      case MENU_ITEM_KEYS.ADD_USERS:
        openDialog(DIALOG_VARIANT.ADD_USER, { toggleAddUsersPopup: closeDialog });
        break;
      case MENU_ITEM_KEYS.LOGOUT:
        dispatch(logout());
        break;
      default:
        push(link);
    }

    setIsMobileMenuOpen(false);
  };

  const createMenuItems = (itemKeys = []) =>
    itemKeys.map((itemKey) => {
      const isDisabled =
        (itemKey === MENU_ITEM_KEYS.ADD_USERS && pathname.startsWith(routes.manageUsers)) ||
        (itemKey === MENU_ITEM_KEYS.DASHBOARD_PORTFOLIO && !isDashboardPortfolioEnabled);

      return {
        ...MENU_ITEMS_DATA[itemKey],
        isDisabled,
        isActive: activeItemKey === itemKey,
        handleItemClick: () => onItemClick(itemKey),
      };
    });

  const accountMenuItems = createMenuItems(ACCOUNT_ITEM_KEYS);

  const superAdminMenuItems =
    isSuperAdminPermissions &&
    SUPER_ADMIN_PAGES.some((pageLink) => pathname.includes(pageLink)) &&
    !isSelectedOrganisation
      ? createMenuItems(superAdminItemKeys)
      : [];

  const organisationAdminMenuItems =
    isAdminPermissions && isSelectedOrganisation ? createMenuItems(organisationItemKeys) : [];

  const userMenuItems =
    isUserPermissions && isSelectedOrganisation
      ? createMenuItems(getUserItemKeys(isLgUp, isDashboardPortfolioEnabled, hasProjectRelations, hasPersonalJournals))
      : [];

  const sidebarItems = uniqBy([...superAdminMenuItems, ...organisationAdminMenuItems, ...userMenuItems], 'itemKey');

  const mobileMenuItems = sidebarItems.filter(({ itemKey }) => itemKey !== MENU_ITEM_KEYS.ADD_USERS);

  return {
    isMobileMenuOpen,
    toggleIsMobileMenuOpen,
    setIsMobileMenuOpen,
    accountMenuItems,
    sidebarItems,
    mobileMenuItems,
  };
};
